import React, { FC, useContext, useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { Category1Types } from 'enums/category1Enums';
import { FormType } from 'enums/FieldTypeEnums';
import {
  Origins,
  ScopeThreeCategoryOne,
} from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import {
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form';
import FieldsRow from 'shared/components/FieldsRow';
import { GetFormData } from '../rawMaterialsLayoutData';
import { categoryTypeFieldMap } from 'views/Scope3/Category4&9/Category4Layout/category4LayoutData';
import OriginsLayout from './originsLayout';
import { CategoryOptions } from 'shared/components/CreateProductModal/ProductModalForm';
import RequestIngredientModal from 'shared/components/RequestIngredientModal';
import RequestProductionRegionModal from 'shared/components/RequestProductionRegion';
import {
  addOriginHandler,
  removeOriginHandler,
  resetFormValues,
} from 'shared/utils/handleIngredientChange';
import { HelpDeskContext } from 'context/HelpDeskContext';
import HelpdeskService from 'services/HelpdeskService/Helpdesk.service';
import { useParams } from 'react-router-dom';
import isProductDisabled from 'shared/utils/isProductDisabled';
import DashboardService from 'services/DashboardService/dashboard.service';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';

interface FertilisersFieldsProps {
  index: number;
  type: Category1Types;
  nameOptions?: CategoryOptions[];
  bestFitRegionOptions?: CategoryOptions[];
  originsLength?: number;
  ingredientsLength?: number;
  remove?: UseFieldArrayRemove;
  originIndex?: number;
  removeOrigin?: (index?: number) => void;
  setIsAddRegion?: React.Dispatch<React.SetStateAction<boolean>>;
  setValue?: UseFormSetValue<ScopeThreeCategoryOne>;
  handleChange?: () => void;
  loading?: boolean;
}

const FertilisersFields: FC<FertilisersFieldsProps> = props => {
  const {
    type,
    index,
    nameOptions,
    remove,
    loading,
  } = props;
  const { bestFitRegion, fetchIngredientOrigin, resetBestFitRegion } =
    Stage1AndStage2ProductService();
  const { fetchActivityMapping, activityMapping } = DashboardService();
  const { control, watch, setValue, trigger } = useFormContext();
  const { setHelpDeskData } = useContext(HelpDeskContext);
  const { fetchData } = HelpdeskService(setHelpDeskData);
  const { status } = useParams();
  const typeValue = categoryTypeFieldMap[type];
  const [isAddIngredient, setIsAddIngredient] = useState<boolean>(false);
  const ingreditentName = watch(`${typeValue}.${index}.activityLabel`);
  const [showBestFitRegionModal, setShowBestFitRegionModal] =
    useState<boolean>(false);
  useEffect(() => {
    fetchMetaValues();
  }, [ingreditentName]);
  const fetchMetaValues = () => {
    if (!ingreditentName) {
      resetBestFitRegion();
      return;
    }
    fetchActivityMapping({
      activity_label: ingreditentName,
    });
    fetchIngredientOrigin({
      activity_label: ingreditentName,
    });
    fetchActivityMapping({
      activity_label: ingreditentName,
    });
  };

  const {
    fields: originFields,
    append: appendOrigin,
    remove: removeOrigin,
  } = useFieldArray({
    control,
    name: `${typeValue}.${index}.origins`,
  });

  const ingredients = watch(`${typeValue}`);
  const isDisabled = isProductDisabled(status);

  const handleAddOrigin = async () => {
    if (watch(`${typeValue}.${index}.origins`).length === 1) {
      addOriginHandler(typeValue, index, watch, setValue);
      await trigger(`${typeValue}.${index}.addressLabel`);
      await trigger(`${typeValue}.${index}.activityMappingRegionSpecId`);
    }
    appendOrigin?.(new Origins(), { shouldFocus: false });
  };

  const handleCloseRequestIngredientModal = () => setIsAddIngredient(false);
  const toggleBestFitRegion = () => setShowBestFitRegionModal(false);

  const handleIngredientChange = async () => {
    const activeIngredient = watch(`${typeValue}.${index}.activityLabel`);
    fetchActivityMapping({
      activity_label: activeIngredient,
    });
    resetFormValues(typeValue, index, origins, setValue, trigger);
  };

  const deleteOrigin = async (originInd?: number) => {
    removeOrigin(originInd);
    if (watch(`${typeValue}.${index}.origins`)?.length === 1) {
      removeOriginHandler(typeValue, index, watch, setValue);
      await trigger(`${typeValue}.${index}.addressLabel`);
      await trigger(`${typeValue}.${index}.activityMappingRegionSpecId`);
    }
  };

  const formData = GetFormData({
    index,
    type,
    originsLength: watch(`${typeValue}.${index}.origins`)?.length,
    ingredientsLength: ingredients?.length,
    remove,
    appendOrigin: handleAddOrigin,
    nameOptions,
    setIsAddIngredient,
    setIsAddRegion: setShowBestFitRegionModal,
    bestFitRegionOptions: bestFitRegion,
    setValue,
    handleChange: handleIngredientChange,
    loading,
    fetchData,
    isDisabled,
  });
  // for triggering validation for share field
  const handleOriginShare = () => {
    originFields?.forEach((_, itemIndex) => {
      trigger?.(`${typeValue}.${index}.origins.${itemIndex}.share`);
    });
  };
  const origins = watch(`${typeValue}.${index}.origins`);

  const ingredientId: string =
    activityMapping?.length > 0 ? activityMapping[0]?.id?.toString() ?? '' : '';

  return (
    <Row data-testid="fertilizers-fields">
      <Col span={24}>
        <FieldsRow {...formData} formType={FormType.REACT_HOOK_FORMS} />
      </Col>
      <Col span={24}>
        {originFields?.map((origin, originIndex: number) => {
          if (origins?.length === 1) return;
          return (
            <OriginsLayout
              key={origin.id}
              index={index}
              removeOrigin={deleteOrigin}
              type={type}
              originIndex={originIndex}
              bestFitRegion={bestFitRegion}
              setValue={setValue}
              setIsAddRegion={setShowBestFitRegionModal}
              loading={loading}
              originsLength={origins?.length}
              isDisabled={isDisabled}
              handleOriginShare={handleOriginShare}
            />
          );
        })}
      </Col>
      <RequestIngredientModal
        showModalState={isAddIngredient}
        handleModalCancel={handleCloseRequestIngredientModal}
        handleModalConfirm={handleCloseRequestIngredientModal}
        isPackageMaterial={type === Category1Types.PACKAGING_MATERIALS}
        isFertilisers={type === Category1Types.FERTILISERS}
        isFarmbox
      />
      <RequestProductionRegionModal
        showModalState={showBestFitRegionModal}
        handleModalCancel={toggleBestFitRegion}
        handleModalConfirm={toggleBestFitRegion}
        ingredientName={ingreditentName ?? ''}
        ingredientId={ingredientId}
        isFarmbox
      />
    </Row>
  );
};

export default FertilisersFields;
